import React from "react";
import { RefreshCw, CheckCircle2 } from "lucide-react";
import { useLang } from "@/lib/useLang";
import { getCandidateName } from "@/lib/i18n";

const COLOR_MAP = {
  saffron: "#FF9933",
  green:   "#138808",
  red:     "#E3000F",
};

export const ResultsView = ({ data, votedFor, onRefresh, refreshing }) => {
  const { t, lang } = useLang();
  if (!data) return null;

  const sorted = [...data.candidates].sort((a, b) => b.votes - a.votes);
  const leader = sorted[0];

  return (
    <div className="space-y-10" data-testid="results-view">
      <div className="brutal-card p-6 md:p-8 fade-up">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600">
              {t("vote_counted")}
            </p>
            <h2
              className="font-display uppercase leading-none mt-2"
              style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}
              data-testid="results-heading"
            >
              {t("live_results")}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600">
                {t("total_votes")}
              </p>
              <p
                className="font-display"
                style={{ fontSize: "3rem", lineHeight: 1 }}
                data-testid="total-votes"
              >
                {data.total_votes.toLocaleString()}
              </p>
            </div>
            <button
              onClick={onRefresh}
              className="brutal-btn px-4 py-4"
              disabled={refreshing}
              data-testid="refresh-results-btn"
              aria-label="Refresh results"
            >
              <RefreshCw size={20} strokeWidth={3} className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {sorted.map((c, i) => {
          const color = COLOR_MAP[c.color];
          const isLeader = i === 0 && data.total_votes > 0;
          const isMine = votedFor === c.id;
          const displayName = getCandidateName(c, lang);
          return (
            <div
              key={c.id}
              className="brutal-card p-5 md:p-6 fade-up"
              style={{ animationDelay: `${i * 120}ms` }}
              data-testid={`result-row-${c.id}`}
            >
              <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-4">
                  <div
                    className="border-4 border-black overflow-hidden flex items-center justify-center font-display shrink-0"
                    style={{
                      background: color,
                      color: c.color === "saffron" ? "#0A0A0A" : "#FFFFFF",
                      width: 72, height: 72, fontSize: "2rem", lineHeight: 1,
                    }}
                    aria-hidden="true"
                    data-testid={`result-avatar-${c.id}`}
                  >
                    {c.photo ? (
                      <img
                        src={c.photo}
                        alt=""
                        crossOrigin="anonymous"
                        style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "50% 18%" }}
                      />
                    ) : (
                      c.initials
                    )}
                  </div>
                  <div>
                    <h3
                      className="font-display uppercase leading-none"
                      style={{ fontSize: "1.75rem" }}
                    >
                      {displayName}
                    </h3>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      {isLeader && (
                        <span
                          className="text-xs uppercase font-extrabold tracking-[0.25em] px-2 py-1 border-2 border-black"
                          style={{ background: "#0A0A0A", color: "#FF9933" }}
                          data-testid={`leader-tag-${c.id}`}
                        >
                          {t("leading")}
                        </span>
                      )}
                      {isMine && (
                        <span
                          className="text-xs uppercase font-extrabold tracking-[0.25em] px-2 py-1 border-2 border-black inline-flex items-center gap-1"
                          style={{ background: "#FFFFFF", color: "#0A0A0A" }}
                          data-testid={`your-vote-tag-${c.id}`}
                        >
                          <CheckCircle2 size={14} strokeWidth={3} /> {t("your_vote")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p
                    className="font-display leading-none"
                    style={{ fontSize: "3.5rem" }}
                    data-testid={`result-pct-${c.id}`}
                  >
                    {c.percentage.toFixed(1)}%
                  </p>
                  <p
                    className="text-sm uppercase font-bold tracking-[0.2em] mt-1"
                    data-testid={`result-count-${c.id}`}
                  >
                    {c.votes.toLocaleString()} {t("votes_word")}
                  </p>
                </div>
              </div>

              <div className="border-4 border-black h-12 md:h-14 bg-neutral-100 overflow-hidden">
                <div
                  className="bar-fill h-full"
                  style={{
                    width: `${Math.max(c.percentage, 0)}%`,
                    background: color,
                    animationDelay: `${300 + i * 120}ms`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <p
        className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600 text-center pt-2"
        data-testid="poll-note"
      >
        {t("poll_note")}
      </p>

      {leader && data.total_votes > 0 && (
        <div className="sr-only" data-testid="leader-name">{getCandidateName(leader, lang)}</div>
      )}
    </div>
  );
};

export default ResultsView;
